import { QuoteLineItemProps } from '@partsbadger/types';

import { buildSummaryPartGroups } from '../components/Checkout/summaryGrouping';
import { getDimensionsState } from './partGeometry';
import { getMissingSpecs, PartErrorField } from './partStatus';
import { getPricingState } from './pricingState';

export type ReviewStatus = 'ready' | 'missing-specs' | 'analysis-pending' | 'manual-quote';

export interface ReviewSpec {
    label: 'Material' | 'Finish' | 'Coating';
    requested: string | null;
    selected: string | null;
}

export interface ReviewVariant {
    id: string;
    lineItemId: number;
    quantities: Array<number>;
    specs: Array<ReviewSpec>;
    status: ReviewStatus;
    statusLabel: string;
    missingSpecs: Array<string>;
}

export interface ReviewPart {
    masterProductId: number;
    name: string;
    customerNotes: string | null;
    variants: Array<ReviewVariant>;
}

const displaySelectedSpec = (item: QuoteLineItemProps, spec: 'material' | 'finish' | 'coating'): string | null => {
    const selected = item[spec];
    const custom = item.product[`custom_${spec}` as 'custom_material' | 'custom_finish' | 'custom_coating'];
    if (selected?.name === 'Custom') return custom || 'Custom';
    return selected?.name ?? null;
};

const requestedSpec = (item: QuoteLineItemProps, spec: 'material' | 'finish' | 'coating'): string | null => {
    const files2d = item.product.attachments?.files_2d;
    const field = {
        material: 'suggested_materials_text',
        finish: 'suggested_finishes_text',
        coating: 'suggested_coatings_text',
    }[spec];
    return files2d?.[field] ?? null;
};

/**
 * The status of a variant. A variant with several quantities is one line item per quantity, so
 * every quantity counts: a variant is not ready while one of them is still being priced, and it
 * is a manual quote when one of them needs one.
 */
const reviewStatus = (
    item: QuoteLineItemProps,
    items: Array<QuoteLineItemProps>,
    errorFields: Array<PartErrorField>,
    lineItemIds: Array<number>
): Pick<ReviewVariant, 'status' | 'statusLabel' | 'missingSpecs'> => {
    const missingSpecs = getMissingSpecs(errorFields, lineItemIds);
    if (missingSpecs.length) {
        return {
            status: 'missing-specs',
            statusLabel: `Missing ${missingSpecs.join(', ')}`,
            missingSpecs,
        };
    }

    const pricingStates = items.map(getPricingState);
    if (getDimensionsState(item.product) === 'pending' || pricingStates.includes('calculating')) {
        return { status: 'analysis-pending', statusLabel: 'Analysis pending', missingSpecs: [] };
    }

    if (pricingStates.includes('manual')) {
        return { status: 'manual-quote', statusLabel: 'Manual quote', missingSpecs: [] };
    }

    return { status: 'ready', statusLabel: 'Ready', missingSpecs: [] };
};

/** Read-only data model for the staff Review Summary view. */
export const buildReviewParts = (
    lineItems: Array<QuoteLineItemProps>,
    errorFields: Array<PartErrorField>
): Array<ReviewPart> =>
    buildSummaryPartGroups(lineItems).map(part => ({
        masterProductId: Number(part.key),
        name: part.baseItem.product.name || part.baseItem.name || `Part ${part.key}`,
        customerNotes: part.baseItem.product.customer_notes,
        variants: part.variants.map(variant => {
            const item = variant.baseItem;
            const lineItemIds = variant.items.map(entry => entry.id);
            return {
                id: variant.id,
                lineItemId: item.id,
                quantities: variant.items.map(entry => entry.quantity),
                specs: ([
                    ['Material', 'material'],
                    ['Finish', 'finish'],
                    ['Coating', 'coating'],
                ] as const).map(([label, spec]) => ({
                    label,
                    requested: requestedSpec(item, spec),
                    selected: displaySelectedSpec(item, spec),
                })),
                ...reviewStatus(item, variant.items, errorFields, lineItemIds),
            };
        }),
    }));
